import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, Check, X, ArrowRight } from "lucide-react";
import { getPaymentStatus } from "../lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 20; // ~40 seconds

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!session_id) return;
    let cancelled = false;
    let attemptCount = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await getPaymentStatus(session_id);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === "paid" || data.status === "failed") return;
      } catch (err) {
        console.warn("Payment status poll failed:", err);
      }
      attemptCount += 1;
      setAttempts(attemptCount);
      if (attemptCount >= MAX_ATTEMPTS) {
        setTimedOut(true);
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => { cancelled = true; };
  }, [session_id]);

  if (!session_id) {
    return (
      <div className="mx-auto max-w-[900px] px-6 py-24 md:px-10 text-center">
        <h1 className="font-display text-6xl uppercase text-white">Missing session</h1>
        <Link to="/premium" className="mt-6 inline-block border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black">Back to Premium</Link>
      </div>
    );
  }

  const paid = status?.payment_status === "paid";
  const isThemes = status?.lookup_key === "premium_themes";
  const isSubscription = status?.lookup_key === "pro_pass_monthly";
  const isGift = status?.lookup_key === "pro_pass_gift_month";
  const isPro = isSubscription || isGift;
  const isUnlock = isThemes || isPro;
  let headline = "Thank you";
  if (isSubscription) headline = "Pro Pass Activated";
  else if (isGift) headline = "Gift Sent";
  else if (isThemes) headline = "Themes Unlocked";
  let body = "";
  if (isSubscription) {
    body = "Your Pro Pass is live — Persona, History, Discover, Schedule, and Import are all unlocked. Manage or cancel anytime from Premium.";
  } else if (isGift) {
    body = "The recipient will see 30 days of Pro Pass the next time they visit Otaku//Index in the browser tied to their handle. No auto-renewal for them.";
  } else if (isThemes) {
    body = "Premium persona-card themes are now available every time you export from this browser.";
  } else if (status) {
    body = `We got your ${((status.amount || 0) / 100).toFixed(2)} ${status.currency?.toUpperCase() || ""} tip. Genuinely — thanks for supporting the app.`;
  }
  let ctaLink = "/personality";
  let ctaLabel = "Back to Persona";
  if (isSubscription) { ctaLink = "/schedule"; ctaLabel = "Explore Pro features"; }
  else if (isGift) { ctaLink = "/premium"; ctaLabel = "Back to Premium"; }
  else if (isThemes) { ctaLink = "/personality"; ctaLabel = "Try new themes"; }

  return (
    <div className="mx-auto max-w-[900px] px-6 py-24 md:px-10">
      {!paid && !timedOut && (
        <div data-testid="payment-loading" className="border border-white/15 bg-neutral-950 p-10 text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#FF3B30]" />
          <h1 className="mt-6 font-display text-4xl uppercase text-white">Confirming payment...</h1>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Attempt {attempts + 1} / {MAX_ATTEMPTS}
          </p>
        </div>
      )}

      {paid && (
        <div data-testid="payment-success" className="border border-emerald-500/40 bg-emerald-500/5 p-10 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center border-2 border-emerald-400 bg-emerald-500/10">
            <Check className="h-8 w-8 text-emerald-400" />
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-emerald-400">// Success</p>
          <h1 className="mt-2 font-display text-6xl uppercase text-white md:text-7xl">
            {headline}
          </h1>
          <p className="mt-4 text-neutral-300">
            {body}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              data-testid="success-cta-personality"
              to={ctaLink}
              className="flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
            >
              {ctaLabel} <ArrowRight className="h-3 w-3" />
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 border border-white/20 bg-transparent px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
            >
              Catalogue
            </Link>
          </div>
        </div>
      )}

      {timedOut && !paid && (
        <div data-testid="payment-timeout" className="border border-amber-500/40 bg-amber-500/5 p-10 text-center">
          <h1 className="font-display text-4xl uppercase text-white">Still confirming</h1>
          <p className="mt-3 text-neutral-300">
            Stripe hasn&apos;t confirmed the payment yet — most of the time this means it went through and just needs a moment. Check your email for a receipt, or refresh this page in a minute. If nothing shows up on your card statement, you weren&apos;t charged.
          </p>
          <Link to="/premium" className="mt-6 inline-block border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black">Back to Premium</Link>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="mx-auto max-w-[900px] px-6 py-24 md:px-10 text-center" data-testid="payment-cancel">
      <div className="mx-auto flex h-14 w-14 items-center justify-center border-2 border-neutral-500">
        <X className="h-8 w-8 text-neutral-400" />
      </div>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-neutral-500">// No charge</p>
      <h1 className="mt-2 font-display text-6xl uppercase text-white md:text-7xl">All good</h1>
      <p className="mt-3 text-neutral-400">You backed out of checkout — nothing was charged, no card details saved. Come back anytime.</p>
      <Link to="/premium" data-testid="cancel-back-btn" className="mt-6 inline-block border border-white/20 bg-[#FF3B30] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black">
        Back to Premium
      </Link>
    </div>
  );
}
